import { useCallback, useLayoutEffect, useRef, type ReactElement } from 'react';

import drawBatteryLifeForecastChart from './utils/draw-battery-life-forecast-chart';
import styles from './style.module.scss';

export interface ChartProperties {
  readonly currentSoh?: number;
  readonly currentAgeMonths: number;
  readonly estimatedSoh8Years?: number;
  readonly estimatedSoh12Years?: number;
  readonly estimatedSoh15Years?: number;
  readonly estimatedTotalYears?: number;
}

const Chart = ({
  currentSoh,
  currentAgeMonths,
  estimatedSoh8Years,
  estimatedSoh12Years,
  estimatedSoh15Years,
  estimatedTotalYears,
}: ChartProperties): ReactElement => {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const viewportRef = useRef<HTMLDivElement | null>(null);

  const redraw = useCallback((): void => {
    if (!svgRef.current || !viewportRef.current) return;

    drawBatteryLifeForecastChart({
      containerElement: viewportRef.current,
      svgElement: svgRef.current,
      currentSoh,
      currentAgeMonths,
      estimatedSoh8Years,
      estimatedSoh12Years,
      estimatedSoh15Years,
      estimatedTotalYears,
    });
  }, [currentSoh, currentAgeMonths, estimatedSoh8Years, estimatedSoh12Years, estimatedSoh15Years, estimatedTotalYears]);

  useLayoutEffect(() => {
    redraw();

    const viewportElement = viewportRef.current;
    if (!viewportElement) return;

    const resizeObserver = new ResizeObserver(() => {
      redraw();
    });

    resizeObserver.observe(viewportElement);

    return () => {
      resizeObserver.disconnect();
    };
  }, [redraw]);

  const hasData = currentSoh !== undefined && estimatedSoh8Years !== undefined && estimatedSoh12Years !== undefined;

  return (
    <div className={styles.container}>
      <div className={styles['top-row']}>
        <div className={styles.unit}>SoH [%]</div>
        {hasData && (
          <div className={styles['prediction-note']} role="note">
            <span className={styles['prediction-note-icon']} aria-hidden="true">
              i
            </span>
            <span>Prediction accuracy may be reduced for low-mileage vehicles.</span>
          </div>
        )}
      </div>
      <div ref={viewportRef} className={styles.viewport} data-testid="battery-life-chart-viewport">
        <svg ref={svgRef} className={styles.svg} role="img" />
      </div>
    </div>
  );
};

export default Chart;
